"use client";

import { Close } from "@ellaandil/ui";
import { Slot } from "@trydig/ui";
import { type ComponentProps, Suspense } from "react";
import {
	DrawerContent,
	DrawerFooter,
	DrawerMain,
	DrawerRoot,
	DrawerViewport,
} from "@/components/ui/Drawer";
import { IconButton } from "@/components/ui/IconButton";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";
import { useModal } from "@/lib/hooks/useModal";
import { CartItems } from "./CartItems";
import { CartSummary } from "./CartSummary";
import { CartUpsell } from "./CartUpsell";
import { EmptyCart } from "./EmptyCart";
import { useCart } from "./hooks";

export const CartModal = () => {
	const { isOpen, setOpen } = useModal("cart");
	const { data } = useCart();
	const t = useTranslations("cart");

	const count = data?.count ?? 0;

	return (
		<DrawerRoot open={isOpen} onOpenChange={setOpen}>
			<DrawerViewport className="!max-w-[600px] bg-background-bg-sand-1 backdrop:bg-overlay-3 backdrop:backdrop-blur-[2.5px]">
				<DrawerContent className="!px-0 !gap-0">
					<header className="flex h-12 shrink-0 items-center px-grid-component-spacing-md">
						<div className="flex h-full flex-1 items-center justify-between border-b border-border-medium">
							<div className="flex flex-1 items-baseline">
								<Typography variant="body-xs-reg" as="h2" className="text-text-dark-text-1">
									{t("header")}
								</Typography>
								<sup className="ml-1 font-body font-normal text-[0.484rem] leading-[1.15] tabular-nums lining-nums text-text-dark-text-1">
									{count}
								</sup>
							</div>
							<IconButton
								icon={Close}
								label={t("close")}
								variant="ghost-primary"
								size="lg"
								className="size-9"
								onClick={() => setOpen(false)}
							/>
						</div>
					</header>
					<DrawerMain className="px-grid-component-spacing-md">
						{data?.hasItems ? (
							<div className="flex flex-col gap-grid-component-spacing-2xl py-grid-component-spacing-2xl">
								<CartItems />
								<CartUpsell />
							</div>
						) : (
							<EmptyCart onContinue={() => setOpen(false)} />
						)}
					</DrawerMain>
					<DrawerFooter className="px-grid-component-spacing-md pb-grid-component-spacing-md">
						<CartSummary />
					</DrawerFooter>
				</DrawerContent>
			</DrawerViewport>
		</DrawerRoot>
	);
};

export interface CartTriggerProps extends ComponentProps<"button"> {
	asChild?: boolean;
}

const UnsuspendedCartTrigger = ({ asChild, children, ...props }: CartTriggerProps) => {
	const { toggle } = useModal("cart");

	const Component = asChild ? Slot : "button";

	return (
		<Component {...props} onClick={toggle}>
			{children}
		</Component>
	);
};

export const CartTrigger: typeof UnsuspendedCartTrigger = (props) => {
	return (
		<Suspense>
			<UnsuspendedCartTrigger {...props} />
		</Suspense>
	);
};
